import {
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    FileText,
    Lightbulb,
    Mail,
    Users,
    Variable,
    Zap,
} from 'lucide-react'
import { useState } from 'react'
import { Typogaphy } from 'vnetwork-ui'

interface SectionProps {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
    defaultOpen?: boolean
}

const Section: React.FC<SectionProps> = ({
    title,
    icon,
    children,
    defaultOpen = false,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen)

    return (
        <div className="border rounded-md overflow-hidden mb-2">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
                <span className="text-primary">{icon}</span>
                <Typogaphy className="font-semibold text-gray-800 flex-1">
                    {title}
                </Typogaphy>
                {isOpen ? (
                    <ChevronDown size={20} className="text-gray-500" />
                ) : (
                    <ChevronRight size={20} className="text-gray-500" />
                )}
            </button>
            {isOpen && <div className="p-4 bg-white">{children}</div>}
        </div>
    )
}

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
    <code className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-mono">
        {children}
    </code>
)

const ImagePlaceholder: React.FC<{ alt: string; src?: string }> = ({
    alt,
    src,
}) => {
    if (src) {
        return (
            <div className="my-4 border rounded-md overflow-hidden">
                <img src={src} alt={alt} className="w-full" />
                <div className="bg-gray-50 px-3 py-2 text-sm text-gray-600 text-center">
                    {alt}
                </div>
            </div>
        )
    }

    return (
        <div className="my-4 border-2 border-dashed border-gray-300 rounded-md p-8 text-center bg-gray-50">
            <div className="text-gray-400 text-sm">[Hình: {alt}]</div>
        </div>
    )
}

const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md my-3">
        <Lightbulb size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">{children}</div>
    </div>
)

const Warning: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex gap-3 p-3 bg-orange-50 border border-orange-200 rounded-md my-3">
        <AlertCircle
            size={20}
            className="text-orange-600 flex-shrink-0 mt-0.5"
        />
        <div className="text-sm text-orange-800">{children}</div>
    </div>
)

const StepList: React.FC<{ steps: string[] }> = ({ steps }) => (
    <ol className="list-decimal list-inside space-y-2 my-3">
        {steps.map((step, index) => (
            <li key={index} className="text-gray-700">
                {step}
            </li>
        ))}
    </ol>
)

const UserGuide: React.FC = () => {
    return (
        <div className="w-full mt-2">
            {/* Tổng quan */}
            <Section
                title="1. Tổng Quan"
                icon={<Mail size={20} />}
                defaultOpen={true}
            >
                <p className="text-gray-700 mb-2">
                    Mailer Campaign là công cụ gửi email marketing hàng loạt đến
                    khách hàng. Hệ thống bao gồm 2 phần chính:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-purple-50 rounded-md border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText size={18} className="text-purple-600" />
                            <strong className="text-purple-800">
                                Template
                            </strong>
                        </div>
                        <p className="text-sm text-purple-700">
                            Mẫu email có thể tái sử dụng, chứa nội dung và các
                            biến động (variables) để cá nhân hóa email.
                        </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Mail size={18} className="text-blue-600" />
                            <strong className="text-blue-800">Campaign</strong>
                        </div>
                        <p className="text-sm text-blue-700">
                            Chiến dịch gửi email, bao gồm template, danh sách
                            người nhận và cấu hình gửi.
                        </p>
                    </div>
                </div>
                <ImagePlaceholder alt="Giao diện tổng quan Mailer Campaign" />
            </Section>

            {/* Tạo Template */}
            <Section
                title="2. Tạo Template Email"
                icon={<FileText size={20} />}
            >
                <h4 className="font-semibold text-gray-800 mb-3">
                    2.1. Các bước tạo Template
                </h4>
                <StepList
                    steps={[
                        'Nhấn nút "Tạo Template" ở góc trên bên phải',
                        'Nhập tiêu đề email (Subject)',
                        'Soạn nội dung email trong editor',
                        'Thêm các biến (variables) để cá nhân hóa nội dung',
                        'Đính kèm file nếu cần (Attachments)',
                        'Nhấn "Lưu Template"',
                    ]}
                />
                <ImagePlaceholder alt="Màn hình tạo template mới" />

                <h4 className="font-semibold text-gray-800 mb-3 mt-6">
                    2.2. Sử dụng Variables trong Template
                </h4>
                <p className="text-gray-700 mb-3">
                    Variables là các biến động sẽ được thay thế bằng dữ liệu
                    thực khi gửi email. Cách sử dụng:
                </p>
                <StepList
                    steps={[
                        'Trong editor, nhấn nút "Insert Variable" hoặc gõ {{',
                        'Chọn biến từ danh sách có sẵn hoặc tạo biến mới',
                        'Biến sẽ hiển thị dạng chip trong editor',
                    ]}
                />

                <div className="bg-gray-50 p-4 rounded-md my-4">
                    <p className="text-sm text-gray-600 mb-2">Ví dụ:</p>
                    <div className="bg-white p-3 rounded border text-sm">
                        Kính gửi <CodeBlock>{'{{customer_name}}'}</CodeBlock>,
                        <br />
                        <br />
                        Cảm ơn quý khách đã sử dụng dịch vụ của{' '}
                        <CodeBlock>{'{{customer_company}}'}</CodeBlock>...
                    </div>
                </div>

                <Tip>
                    Bạn có thể tạo biến tùy chỉnh bằng cách nhập tên biến mới.
                    Đảm bảo tên biến không có dấu cách và ký tự đặc biệt.
                </Tip>

                <h4 className="font-semibold text-gray-800 mb-3 mt-6">
                    2.3. Đính kèm file (Attachments)
                </h4>
                <p className="text-gray-700 mb-3">
                    Để đính kèm file vào email:
                </p>
                <StepList
                    steps={[
                        'Nhấn nút "Đính kèm" trong phần Attachments',
                        'Chọn file từ máy tính (hỗ trợ PDF, DOC, XLS, hình ảnh...)',
                        'File sẽ được upload và hiển thị trong danh sách',
                        'Nhấn X để xóa file đính kèm',
                    ]}
                />
                <Warning>
                    Tổng dung lượng file đính kèm không nên vượt quá 10MB để đảm
                    bảo email được gửi thành công.
                </Warning>
            </Section>

            {/* Tạo Campaign */}
            <Section title="3. Tạo Campaign" icon={<Mail size={20} />}>
                <h4 className="font-semibold text-gray-800 mb-3">
                    3.1. Thông tin cơ bản
                </h4>
                <StepList
                    steps={[
                        'Nhấn nút "Tạo Campaign"',
                        'Nhập tên campaign và mô tả (tùy chọn)',
                        'Chọn Template email đã tạo sẵn',
                    ]}
                />
                <ImagePlaceholder alt="Màn hình tạo campaign - Thông tin cơ bản" />

                <h4 className="font-semibold text-gray-800 mb-3 mt-6">
                    3.2. Chọn nguồn người nhận (Receivers)
                </h4>
                <p className="text-gray-700 mb-3">
                    Có 2 cách để chọn danh sách người nhận:
                </p>

                <div className="space-y-4">
                    {/* Internal Source */}
                    <div className="p-4 bg-green-50 rounded-md border border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                            <Users size={18} className="text-green-600" />
                            <strong className="text-green-800">
                                A. Internal (Từ hệ thống)
                            </strong>
                        </div>
                        <p className="text-sm text-green-700 mb-3">
                            Lấy danh sách khách hàng trực tiếp từ database dựa
                            trên các bộ lọc:
                        </p>
                        <ul className="list-disc list-inside text-sm text-green-700 space-y-1 ml-2">
                            <li>
                                <strong>Trạng thái khách hàng:</strong>{' '}
                                Coollead, POC, Trial, Active, Stop...
                            </li>
                            <li>
                                <strong>Ngành nghề:</strong> Lọc theo industry
                            </li>
                            <li>
                                <strong>Nhóm sản phẩm:</strong> Hiện khi chọn
                                Active/Trial/Stop
                            </li>
                            <li>
                                <strong>Trạng thái dịch vụ:</strong> Hiện khi
                                chọn 2+ trạng thái Active/Trial/Stop
                            </li>
                            <li>
                                <strong>Dịch vụ quan tâm:</strong> Hiện khi chọn
                                Coollead
                            </li>
                            <li>
                                <strong>Nhân viên phụ trách:</strong> Lọc theo
                                sale follower
                            </li>
                        </ul>
                        <Tip>
                            Nhấn "Xem trước kết quả" để kiểm tra số lượng và
                            danh sách khách hàng phù hợp với bộ lọc.
                        </Tip>
                    </div>

                    {/* External Source */}
                    <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText size={18} className="text-blue-600" />
                            <strong className="text-blue-800">
                                B. External (Import file)
                            </strong>
                        </div>
                        <p className="text-sm text-blue-700 mb-3">
                            Upload file Excel/CSV chứa danh sách người nhận:
                        </p>
                        <StepList
                            steps={[
                                'Chọn "External (Import)"',
                                'Tải file Excel hoặc CSV',
                                'Map các cột trong file với variables',
                                'Kiểm tra preview dữ liệu',
                            ]}
                        />
                        <Warning>
                            File phải có cột <CodeBlock>email</CodeBlock> - đây
                            là trường bắt buộc.
                        </Warning>
                    </div>
                </div>

                <ImagePlaceholder alt="Chọn nguồn người nhận - Internal vs External" />

                <h4 className="font-semibold text-gray-800 mb-3 mt-6">
                    3.3. Cấu hình SMTP (Tùy chọn)
                </h4>
                <p className="text-gray-700 mb-3">
                    Mặc định hệ thống sử dụng SMTP server chung. Nếu muốn dùng
                    SMTP riêng:
                </p>
                <StepList
                    steps={[
                        'Bật toggle "Sử dụng SMTP tùy chỉnh"',
                        'Nhập thông tin: Server, Username, Password',
                        'Nhấn "Test kết nối" để kiểm tra',
                    ]}
                />

                <h4 className="font-semibold text-gray-800 mb-3 mt-6">
                    3.4. Thông tin người gửi (Tùy chọn)
                </h4>
                <p className="text-gray-700 mb-3">
                    Thêm thông tin người gửi để hiển thị trong signature:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                    <li>Tên người gửi</li>
                    <li>Giới tính</li>
                    <li>Số điện thoại</li>
                    <li>Chức vụ</li>
                </ul>
            </Section>

            {/* Variables */}
            <Section
                title="4. Danh Sách Variables Có Sẵn"
                icon={<Variable size={20} />}
            >
                <p className="text-gray-700 mb-2">
                    Khi sử dụng nguồn <strong>Internal</strong>, các biến sau sẽ
                    được tự động điền dữ liệu từ hệ thống:
                </p>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 text-sm">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="border border-gray-200 px-4 py-2 text-left">
                                    Variable
                                </th>
                                <th className="border border-gray-200 px-4 py-2 text-left">
                                    Mô tả
                                </th>
                                <th className="border border-gray-200 px-4 py-2 text-left">
                                    Ví dụ
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="border border-gray-200 px-4 py-2">
                                    <CodeBlock>{'{{email}}'}</CodeBlock>
                                </td>
                                <td className="border border-gray-200 px-4 py-2">
                                    Email khách hàng
                                </td>
                                <td className="border border-gray-200 px-4 py-2 text-gray-500">
                                    contact@company.com
                                </td>
                            </tr>
                            <tr className="bg-gray-50">
                                <td className="border border-gray-200 px-4 py-2">
                                    <CodeBlock>{'{{name}}'}</CodeBlock>
                                </td>
                                <td className="border border-gray-200 px-4 py-2">
                                    Tên khách hàng
                                </td>
                                <td className="border border-gray-200 px-4 py-2 text-gray-500">
                                    Nguyễn Văn A
                                </td>
                            </tr>
                            <tr>
                                <td className="border border-gray-200 px-4 py-2">
                                    <CodeBlock>{'{{company}}'}</CodeBlock>
                                </td>
                                <td className="border border-gray-200 px-4 py-2">
                                    Tên công ty{' '}
                                    <span className="text-gray-400 text-xs">
                                        (nếu là KH công ty)
                                    </span>
                                </td>
                                <td className="border border-gray-200 px-4 py-2 text-gray-500">
                                    Công ty ABC
                                </td>
                            </tr>
                            <tr className="bg-gray-50">
                                <td className="border border-gray-200 px-4 py-2">
                                    <CodeBlock>{'{{phone}}'}</CodeBlock>
                                </td>
                                <td className="border border-gray-200 px-4 py-2">
                                    Số điện thoại{' '}
                                    <span className="text-gray-400 text-xs">
                                        (nếu có)
                                    </span>
                                </td>
                                <td className="border border-gray-200 px-4 py-2 text-gray-500">
                                    0901234567
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Tip>
                    Khi sử dụng nguồn <strong>External (Import)</strong>, bạn có
                    thể tạo các biến tùy chỉnh và map với cột trong file Excel.
                </Tip>
            </Section>

            {/* Gửi Email */}
            <Section title="5. Gửi Email" icon={<Zap size={20} />}>
                <h4 className="font-semibold text-gray-800 mb-3">
                    5.1. Gửi Email Test
                </h4>
                <p className="text-gray-700 mb-3">
                    Trước khi gửi hàng loạt, hãy gửi test để kiểm tra:
                </p>
                <StepList
                    steps={[
                        'Nhập email nhận test (mặc định là email của bạn)',
                        'Nhấn nút "Gửi Test"',
                        'Kiểm tra email trong hộp thư',
                        'Xác nhận nội dung, format, và các biến đã được thay thế đúng',
                    ]}
                />
                <ImagePlaceholder alt="Gửi email test" />

                <Warning>
                    Luôn gửi test trước khi gửi hàng loạt để tránh lỗi không
                    mong muốn!
                </Warning>

                <h4 className="font-semibold text-gray-800 mb-3 mt-6">
                    5.2. Gửi Hàng Loạt (Bulk Delivery)
                </h4>
                <p className="text-gray-700 mb-3">
                    Sau khi đã test thành công:
                </p>
                <StepList
                    steps={[
                        'Kiểm tra lại số lượng người nhận',
                        'Nhấn nút "Bắt đầu gửi"',
                        'Xác nhận trong popup',
                        'Theo dõi tiến độ gửi trong tab "Lịch sử gửi"',
                    ]}
                />

                <div className="p-4 bg-gray-50 rounded-md my-4">
                    <p className="text-sm text-gray-700 mb-2">
                        <strong>Trạng thái gửi:</strong>
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                            Pending - Đang chờ
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Sent - Đã gửi
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-sm">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            Failed - Thất bại
                        </span>
                    </div>
                </div>
            </Section>

            {/* Tips */}
            <Section
                title="6. Tips & Best Practices"
                icon={<CheckCircle2 size={20} />}
            >
                <div className="space-y-4">
                    <div className="flex gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle2
                                size={16}
                                className="text-green-600"
                            />
                        </div>
                        <div>
                            <strong className="text-gray-800">
                                Sử dụng Template có sẵn
                            </strong>
                            <p className="text-sm text-gray-600">
                                Tạo và lưu các template hay dùng để tiết kiệm
                                thời gian cho các campaign sau.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle2
                                size={16}
                                className="text-green-600"
                            />
                        </div>
                        <div>
                            <strong className="text-gray-800">
                                Kiểm tra Variables kỹ
                            </strong>
                            <p className="text-sm text-gray-600">
                                Đảm bảo tất cả biến đều có dữ liệu. Biến không
                                có dữ liệu sẽ hiển thị trống trong email.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle2
                                size={16}
                                className="text-green-600"
                            />
                        </div>
                        <div>
                            <strong className="text-gray-800">
                                Luôn gửi Test trước
                            </strong>
                            <p className="text-sm text-gray-600">
                                Gửi email test để kiểm tra format, nội dung và
                                các biến trước khi gửi hàng loạt.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle2
                                size={16}
                                className="text-green-600"
                            />
                        </div>
                        <div>
                            <strong className="text-gray-800">
                                Sử dụng bộ lọc Internal hợp lý
                            </strong>
                            <p className="text-sm text-gray-600">
                                Kết hợp nhiều bộ lọc để target đúng đối tượng
                                khách hàng, tăng hiệu quả campaign.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle2
                                size={16}
                                className="text-green-600"
                            />
                        </div>
                        <div>
                            <strong className="text-gray-800">
                                Theo dõi kết quả
                            </strong>
                            <p className="text-sm text-gray-600">
                                Kiểm tra tab "Lịch sử gửi" để xem tỷ lệ thành
                                công và xử lý các email thất bại.
                            </p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* FAQ */}
            <Section
                title="7. Câu Hỏi Thường Gặp (FAQ)"
                icon={<AlertCircle size={20} />}
            >
                <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-md">
                        <p className="font-medium text-gray-800 mb-2">
                            Q: Tại sao email không gửi được?
                        </p>
                        <p className="text-sm text-gray-600">
                            A: Kiểm tra: (1) SMTP config đã đúng chưa, (2) Email
                            người nhận có hợp lệ không, (3) Dung lượng file đính
                            kèm có quá lớn không.
                        </p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-md">
                        <p className="font-medium text-gray-800 mb-2">
                            Q: Làm sao để biết email đã gửi thành công?
                        </p>
                        <p className="text-sm text-gray-600">
                            A: Xem trong tab "Lịch sử gửi", email thành công sẽ
                            có trạng thái "Sent" màu xanh.
                        </p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-md">
                        <p className="font-medium text-gray-800 mb-2">
                            Q: Có thể gửi lại email thất bại không?
                        </p>
                        <p className="text-sm text-gray-600">
                            A: Có, bạn có thể retry các email thất bại từ tab
                            "Lịch sử gửi".
                        </p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-md">
                        <p className="font-medium text-gray-800 mb-2">
                            Q: Giới hạn số lượng email gửi mỗi lần?
                        </p>
                        <p className="text-sm text-gray-600">
                            A: Tùy thuộc vào cấu hình SMTP server. Liên hệ admin
                            để biết giới hạn cụ thể.
                        </p>
                    </div>
                </div>
            </Section>
        </div>
    )
}

export default UserGuide
